import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useDashboardChart } from '../hooks/useDashboardStats';

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const periods = [
  { value: 7, label: '7日' },
  { value: 30, label: '30日' },
  { value: 90, label: '90日' },
];

interface ConversationsChartProps {
  domainId?: string | null;
}

export const ConversationsChart: React.FC<ConversationsChartProps> = ({ domainId }) => {
  const [period, setPeriod] = useState<number>(30);
  const { chartData, isLoading } = useDashboardChart(period, domainId);

  const data = chartData?.data?.map((d) => ({
    date: formatDate(d.date),
    利用者: d.conversations,
    訪問者: d.visitors ?? 0,
  })) || [];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="conversations-chart">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">訪問者・利用者の推移</h2>
        <div className="inline-flex rounded-md border border-input" data-testid="conversations-chart-toggle">
          {periods.map((p, index) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={[
                'px-3 py-1 text-sm transition-colors',
                period === p.value ? 'bg-primary text-white' : 'hover:bg-gray-100',
                index === 0 ? 'rounded-l-md' : '',
                index === periods.length - 1 ? 'rounded-r-md' : '',
              ].join(' ')}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : data.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-8 text-text-secondary"
          data-testid="conversations-chart-empty"
        >
          まだ会話データがありません
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="利用者" fill="#0a7a72" />
            <Line type="monotone" dataKey="訪問者" stroke="#f59e0b" dot={false} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
